//
// InitialAssetsProvider
//
// Wraps the entire app above ErrorBoundary. Provides a fully usable
// `translate()` keyed on a small bundled string set, so dead-end
// surfaces rendered before (or instead of) TownAssetsProvider can
// still call translate() through the same TownAssetsContext.
//
// TownAssetsProvider, when mounted below, replaces the context value but
// falls back to these same bundled strings until the server language loads,
// so logged-out surfaces under it still resolve. A key needed in both worlds
// must live in the bundled JSON here; if also shown logged-in, it must also
// live in the server-side language files.
//

import PropTypes from 'prop-types';
import { useMemo } from 'react';

import {
  arrayOrStringToString,
  formatString,
  logError,
  stringUpperCaseFirstLetter,
} from '../../utils';

import initialStringsEn from './initialStringsEn.json';
import initialStringsFr from './initialStringsFr.json';
import TownAssetsContext from './TownAssetsContext';

const BUNDLED = {
  fr: initialStringsFr,
  en: initialStringsEn,
};

const pickLanguage = () => {
  const raw = globalThis.navigator?.language ?? 'fr';
  const code = raw.slice(0, 2).toLowerCase();
  return BUNDLED[code] ? code : 'fr';
};

function InitialAssetsProvider({ children }) {
  const language = useMemo(
    () => BUNDLED[pickLanguage()],
    [],
  );

  const translate = useMemo(
    () => (
      code,
      type = 'generic',
      params,
      capitalize = true,
      ignoreMissing = false,
    ) => {
      if (!code) return '';

      let ret;
      if (language.data?.[type]) {
        if (language.data[type][code]) {
          ret = arrayOrStringToString(language.data[type][code]);
        }

        if (ret) {
          if (params) ret = formatString(ret, params);
          if (capitalize) ret = stringUpperCaseFirstLetter(ret);
          return ret;
        }

        if (language.data[type][code] === null) return null;

        if (!ignoreMissing) {
          logError(
            `InitialAssetsProvider - Missing language type="${
              type}" code="${code}" language.rev="${language.rev}"`,
            'InitialAssetsProvider.translate.missingCode',
            { type, code, languageRev: language?.rev },
          );
        }
      } else if (!ignoreMissing) {
        logError(
          `InitialAssetsProvider - Missing language.data[${type}] (code="${code}")`,
          'InitialAssetsProvider.translate.missingType',
          { type, code, languageRev: language?.rev },
        );
      }

      if (ignoreMissing) return null;
      return `--${code}--`;
    },
    [language],
  );

  const contextValue = useMemo(
    () => ({
      assets: {
        ready: true,
        language,
        translate,
      },
    }),
    [language, translate],
  );

  return (
    <TownAssetsContext.Provider value={contextValue}>
      {children}
    </TownAssetsContext.Provider>
  );
}

InitialAssetsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default InitialAssetsProvider;
